/**
 * Share a generated report as a NO-STORAGE link.
 *
 * Security model — the shared link must never touch or expose the funnel:
 *   - The entire report travels inside the URL FRAGMENT (after `#`). Fragments
 *     are NEVER sent to any server, so opening a share link uploads nothing and
 *     stores nothing anywhere. There is literally no data at rest to leak.
 *   - The public `/r` route renders the report entirely client-side from the
 *     fragment and makes ZERO authenticated / funnel API calls (App.tsx skips
 *     the auth refresh on that route).
 *   - The only data present is what the rep already showed the prospect — their
 *     public industry/size/location + the generated narrative. No org, account,
 *     auth, or raw enrichment data is ever included.
 *
 * Trade-off vs. a stored short link: the URL is long (a few KB). Accepted for
 * the security win of storing nothing.
 */

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Encode a report payload into a URL-fragment-safe string. */
export function encodeReport(payload: unknown): string {
  return toBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

/** Decode a report payload from a URL fragment. Returns null on any error. */
export function decodeReport(hash: string): unknown | null {
  try {
    const clean = hash.replace(/^#/, '');
    if (!clean) return null;
    return JSON.parse(new TextDecoder().decode(fromBase64Url(clean)));
  } catch {
    return null;
  }
}

/** Build the full shareable link for a report payload. */
export function buildShareUrl(payload: unknown): string {
  return `${window.location.origin}/r#${encodeReport(payload)}`;
}
